import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PropertyStatus {
  propertyId: string;
  hasActiveTask: boolean;
  hasLowInventory: boolean;
  hasOpenIssues: boolean;
  hasCleaningToday: boolean;
  hasOverdueClean: boolean;
  hasReplenishmentNeeded: boolean;
}

export const usePropertyStatusBadges = (propertyIds: string[], enabled: boolean = true) => {
  return useQuery({
    queryKey: ["property-status-badges", propertyIds],
    queryFn: async (): Promise<Record<string, PropertyStatus>> => {
      if (!propertyIds.length) return {};

      const today = new Date().toISOString().split("T")[0];

      // Fetch all data in parallel
      const [tasksResult, inventoryResult, issuesResult, suggestionsResult] = await Promise.all([
        // Get all cleaning tasks for these properties with reservation info
        supabase
          .from("cleaning_tasks")
          .select("*")
          .in("property_id", propertyIds),
        
        // Get inventory items with count and fullness data
        supabase
          .from("inventory_items")
          .select("property_id, current_count, in_stock_qty, threshold_count, quantity_type, current_value, threshold_value")
          .in("property_id", propertyIds),
        
        // Get open maintenance issues
        supabase
          .from("maintenance_issues")
          .select("property_id, status")
          .in("property_id", propertyIds)
          .in("status", ["open", "pending", "in_progress"]),
        
        // Get open replenishment suggestions
        supabase
          .from("replenishment_suggestions")
          .select("property_id")
          .in("property_id", propertyIds)
          .eq("status", "open"),
      ]);

      const tasks = tasksResult.data || [];
      const inventory = inventoryResult.data || [];
      const issues = issuesResult.data || [];
      const suggestions = suggestionsResult.data || [];

      // Handle errors gracefully with detailed logging
      if (tasksResult.error) {
        console.error("[usePropertyStatusBadges] Error fetching tasks:", {
          message: tasksResult.error.message,
          code: tasksResult.error.code,
          details: tasksResult.error.details,
        });
      }
      if (inventoryResult.error) {
        console.error("[usePropertyStatusBadges] Error fetching inventory:", {
          message: inventoryResult.error.message,
          code: inventoryResult.error.code,
          details: inventoryResult.error.details,
        });
      }
      if (issuesResult.error) {
        console.error("[usePropertyStatusBadges] Error fetching issues:", {
          message: issuesResult.error.message,
          code: issuesResult.error.code,
          details: issuesResult.error.details,
        });
      }
      if (suggestionsResult.error) {
        console.error("[usePropertyStatusBadges] Error fetching suggestions:", {
          message: suggestionsResult.error.message,
          code: suggestionsResult.error.code,
          details: suggestionsResult.error.details,
        });
      }

      // Helper to detect blocked reservations
      const isBlockedReservation = (reservation: { reservation_type?: string; summary?: string } | null) => {
        if (!reservation) return false;
        if (reservation.reservation_type === "blocked") return true;
        const summary = (reservation.summary || "").toLowerCase();
        return summary.includes("not available") || summary.includes("blocked");
      };

      // Build status map
      const statusMap: Record<string, PropertyStatus> = {};

      propertyIds.forEach((propertyId) => {
        // Filter out tasks for blocked reservations
        const propertyTasks = tasks
          .filter((t) => t.property_id === propertyId)
          .filter((t) => !isBlockedReservation(t.reservation as any));
        const propertyInventory = inventory.filter((i) => i.property_id === propertyId);
        const propertyIssues = issues.filter((i) => i.property_id === propertyId);
        const propertySuggestions = suggestions.filter((s) => s.property_id === propertyId);

        // Active task: scheduled, in_progress, or checklist not completed
        const hasActiveTask = propertyTasks.some(
          (t) =>
            t.status === "scheduled" ||
            t.status === "in_progress" ||
            (t.status !== "completed" && !t.checklist_completed)
        );

        // Low inventory: check both units-based and fullness-based items (units: use In stock only)
        const hasLowInventory = propertyInventory.some((i) => {
          if (i.quantity_type === 'fullness') {
            const currentValue = i.current_value ?? 1.0;
            const thresholdValue = i.threshold_value ?? 0.25;
            return currentValue <= thresholdValue;
          }
          const inStock = i.in_stock_qty ?? i.current_count ?? 0;
          return (
            i.threshold_count !== null &&
            inStock !== null &&
            inStock <= i.threshold_count
          );
        });

        // Open issues
        const hasOpenIssues = propertyIssues.length > 0;

        // Cleaning today
        const hasCleaningToday = propertyTasks.some(
          (t) => t.scheduled_date === today && t.status !== "completed"
        );

        // Overdue: scheduled before today and not completed
        const hasOverdueClean = propertyTasks.some(
          (t) => t.scheduled_date < today && t.status !== "completed"
        );

        // Open replenishment suggestions
        const hasReplenishmentNeeded = propertySuggestions.length > 0;

        statusMap[propertyId] = {
          propertyId,
          hasActiveTask,
          hasLowInventory,
          hasOpenIssues,
          hasCleaningToday,
          hasOverdueClean,
          hasReplenishmentNeeded,
        };
      });

      return statusMap;
    },
    enabled: enabled && propertyIds.length > 0,
    staleTime: 30000, // 30 seconds
  });
};
